import { DateTime } from "luxon";
import type { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { SystemDatePickerField } from "../../../../components/UIToolkit/Inputs/SystemDatePickerField";
import { SettingRow } from "../../../../components/UIToolkit/molecules/SettingRow";
import type { ApplyFromDateFieldCommonProps } from "./ApplyFromDateField";
import { V2_APPLY_FROM_FIELD_WIDTH, v2SettingRowSx } from "./consts";

/**
 * The v2 apply-from date: one line of a settings panel. The title and the helper sit on the left ("Apply this change
 * from" / "Earlier days keep the old numbers."), and on the right the date field (`SystemDatePickerField`), whose
 * calendar starts at today in the department. Same props and handlers as v1: the hook owns the date and the picker's
 * anchor.
 */
export const ApplyFromDateFieldV2: FC<ApplyFromDateFieldCommonProps & { disabled: boolean; timezone: string }> = ({
	updateFromDate,
	onUpdateFromDateChange,
	affectsShifts,
	datePickerAnchorEl,
	onDatePickerAnchorElChange,
	disabled,
	timezone,
}): ReactElement => {
	const { t } = useTranslation();
	const title = t("locations.expanded.applyFrom.title");
	// Off under an edit lock, and when the save can't reach a shift (the date would be accepted, then ignored).
	const off = disabled || !affectsShifts;

	return (
		<SettingRow
			label={title}
			description={t("locations.expanded.applyFrom.helper")}
			dataTestId="location-apply-from-date"
			sx={v2SettingRowSx}
			control={
				<SystemDatePickerField
					value={updateFromDate}
					onChange={onUpdateFromDateChange}
					ariaLabel={title}
					anchorEl={datePickerAnchorEl}
					onAnchorElChange={onDatePickerAnchorElChange}
					disabled={off}
					disabledReason={disabled ? t("schedule.template.edit.blocked") : t("schedule.template.applyFrom.noShiftsAffected")}
					// Today in the department, the day the editor starts the date on: the admin moves shifts from there on.
					minDate={DateTime.now().setZone(timezone).startOf("day")}
					width={V2_APPLY_FROM_FIELD_WIDTH}
					dataTestId="location-apply-from-date-input"
				/>
			}
		/>
	);
};
